import { useBrands, useTemplate } from '#~/device/registry/hooks.js';
import { motion } from '@studio/design-system/motion';
import { getCurrentVendorBadgeIcon, vendorName } from '@studio/design-system/themes/vendor.js';
import { Arrow, Tooltip } from '@studio/design-system/tooltip';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { twMerge } from 'cn';
import type { IrisDeviceCardProps } from './types.js';

/** Size configuration for different variants. */
const sizeConfig = {
  small: {
    container: 'gap-8',
    image: 'w-32 h-32',
    text: 'text-sm',
    badge: 'w-12 h-12'
  },
  medium: {
    container: 'gap-12',
    image: 'w-48 h-48',
    text: 'text-md',
    badge: 'w-16 h-16'
  },
  large: {
    container: 'gap-16',
    image: 'w-64 h-64',
    text: 'text-lg',
    badge: 'w-20 h-20'
  }
} as const;

/** Iris device card component for displaying enhanced device information. */
export const IrisDeviceCard = ({
  sourceSelection,
  showTooltip = true,
  size = 'medium',
  variant = 'bridge-selector',
  showBanner: _showBanner,
  onBannerClose: _onBannerClose,
  className,
  ...props
}: IrisDeviceCardProps): ReactElement => {
  // Get device registry information
  const brands = useBrands();
  const template = useTemplate(sourceSelection?.modelId);

  // Get brand information
  const brand = useMemo(() => {
    if (!template?.model?.brandId || !brands) return undefined;

    const selection = Object.entries(brands?.items ?? {})?.find?.(
      ([, { id }]) => id === template.model.brandId
    );
    return selection?.[1];
  }, [template?.model?.brandId, brands]);

  // Extract device information from sourceSelection
  const deviceInfo = useMemo(() => {
    if (!sourceSelection) {
      return {
        name: 'Unknown Device',
        brandName: 'Unknown Brand',
        modelName: 'Unknown Model',
        version: 'Unknown'
      };
    }

    return {
      name: sourceSelection.name || 'Unknown Device',
      brandName: brand?.name || 'Unknown Brand',
      modelName: template?.model?.name || 'Unknown Model',
      version: sourceSelection.version || 'Unknown'
    };
  }, [sourceSelection, brand, template]);

  const config = sizeConfig[size];

  const shouldRenderBadge = variant === 'bridge-selector';
  const cardConfig = {
    'bridge-selector': {
      containerPadding: 'p-16',
      backgroundColor: 'bg-base-200'
    },
    'monitoring': {
      containerPadding: 'p-12',
      backgroundColor: 'bg-base-100'
    }
  } as const;
  const badgeConfig = {
    size: 'h-32 w-[90px]',
    iconSize: 'h-32 w-32',
    text: 'text-sm',
    position: 'absolute right-6 top-6'
  } as const;
  const vConfig = cardConfig[variant];

  // Create the enabled badge with proper styling
  const enabledBadge = shouldRenderBadge ? (
    <div
      className={twMerge(
        'flex items-center gap-2 rounded-[500px] bg-base-100 py-2 transition-colors',
        'hover:cursor-help hover:bg-dark',
        badgeConfig.size
      )}
    >
      <div className='flex h-full items-center justify-center'>
        <img
          src={
            vendorName() === 'Iris'
              ? '/assets/icons/iris_badge_no_bg.png'
              : getCurrentVendorBadgeIcon()
          }
          alt={`${vendorName()} Logo`}
          className={badgeConfig.iconSize}
        />
      </div>
      <span className={twMerge('font-medium leading-100 text-light', badgeConfig.text)}>
        Enabled
      </span>
    </div>
  ) : null;

  const tooltipContent = (
    <div className='max-w-[320px] rounded-4 bg-[white] p-8 shadow-lg'>
      <div className='text-center text-sm font-medium leading-100 text-[#00102D]'>
        {vendorName()} enabled devices are automatically configured, pre-calibrated, and
        controllable from anywhere in the world - no computer agent required.
      </div>
    </div>
  );

  return (
    <div className={twMerge('flex flex-col gap-12', className)} {...props}>
      {/* Device Card */}
      <div
        className={twMerge(
          'rounded-lg relative overflow-hidden border border-base-100',
          'flex flex-col',
          vConfig.containerPadding,
          vConfig.backgroundColor
        )}
      >
        <div className={twMerge('flex items-center', config.container)}>
          {/* Device Image */}
          <div className={twMerge('flex-shrink-0', config.image)}>
            <img
              src={template?.model?.iconUrl || '/assets/icons/device-placeholder.svg'}
              alt={`${deviceInfo.brandName} ${deviceInfo.modelName}`}
              className='h-full w-full object-contain'
              onError={(e) => {
                // Fallback to placeholder if device icon fails to load
                e.currentTarget.src = '/assets/icons/device-placeholder.svg';
              }}
            />
          </div>

          {/* Device Information */}
          <div className='min-w-0 flex-1'>
            {/* Brand Name */}
            <div
              className={twMerge(
                'mb-1 font-regular text-light',
                config.text === 'text-lg' ? 'text-base' : 'text-md'
              )}
            >
              {deviceInfo.brandName}
            </div>

            {/* Model/Device Name */}
            <h3 className={twMerge('mb-3 truncate text-base font-medium text-light', config.text)}>
              {deviceInfo.modelName}
            </h3>

            {/* Iris Version */}
            <div className='text-md font-medium text-primary'>
              {vendorName()} Version: {deviceInfo.version}
            </div>
          </div>

          {/* Enabled Badge */}
          {enabledBadge ? (
            <div className={twMerge('cursor-pointer', badgeConfig.position)}>
              {showTooltip ? (
                <Tooltip zIndex={100} trigger={enabledBadge}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {tooltipContent}
                  </motion.div>

                  <Arrow className='fill-[white]' />
                </Tooltip>
              ) : (
                enabledBadge
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
